//core
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, FormikHelpers, FormikValues } from 'formik';
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getSymbolFromCode } from "currency-code-symbol-map";
import format from "date-fns/format";
import Button from "../../../../../../UI/Buttons/Button";
import { Alert, Box, Typography, useMediaQuery } from "@mui/material";

//styles
import { useStyles } from "./styles";

//context
import { usePayoutsContext } from "../../PayoutsContext";

//helpers
import { importAll } from "../../../../../../../helpers/importAll";

//selectors
import {
  selectCreatePayoutData,
  selectCreatePayoutError,
  selectGetAvailablePaymentsListData,
  selectUpdatePayoutData,
  selectRemovePayoutData,
  selectUpdatePayoutError
} from "../../../../../../../store/admin/flatFee/selectors";
import { selectUploadConfigData } from "../../../../../../../store/common/configuration/selectors";
import { RootState } from "../../../../../../../store";

//hooks
import { useFlatFee } from "../../../../../../../store/admin/flatFee/useFlatFee";
import useQuery from "../../../../../../../customHooks/useQuery";
import { useConfiguration } from "../../../../../../../store/common/configuration/useConfiguration";

//validation
import { validationTextField } from "../../../../../../validation/textFieldNotRequired.validation";
import { validationTextFieldMinMax } from "../../../../../../validation/textFieldMaxMinNotRequired.validation";
import { validationAmountField } from "../../../../../../validation/amount.validation";
import { validationAmountNotRequiredField } from "../../../../../../validation/amountNotRequired.validation";
import { validationRequiredAutocompleteField } from "../../../../../../validation/requiredAutocompleteStringId.validation";
import { validationSingleSelectField } from "../../../../../../validation/fieldSingleSelect.validation";

//components
import HelperField from "./HelperField";
import MuiTextField from "../../../../../../UI/FormFields/MuiTextField";
import { MuiSingleDateTimePicker } from "../../../../../../UI/FormFields/MuiSingleDateTimePicker";
import SelectIconsAutocomplete from "../../../../../../UI/FormFields/SelectIconsAutocomplete";

const exceptThisSymbols = ["e", "E", "+", "-", ","];

export interface ICreateUpdatePayoutProps {
  closeModal?: (state:boolean) => void;
  data: any;
  affiliateId?: any;
  flatFeeId?: any;
}

const CreateUpdatePayout: React.FC<ICreateUpdatePayoutProps> = ({
  closeModal,
  data,
  affiliateId,
  flatFeeId,
}): JSX.Element => {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const mobile = useMediaQuery('(max-width:767px)');
  const formRef: any = useRef();
  const context = usePayoutsContext();
  let query = useQuery();

  const { getUploadConf } = useConfiguration();
  const { affiliate_id } = data;

  const {
    createPayout,
    updatePayout,
    clearUpdatePayout,
    clearCreatePayout,
    clearRemovePayout,
    getAvailablePaymentsList,
    clearAvailablePaymentsList
  } = useFlatFee();

  const availablePaymentsData = useSelector((state: RootState) =>
    selectGetAvailablePaymentsListData(state, Number(query.get('affiliate_id')) || affiliateId || affiliate_id)
  );
  const uploadConfigData = useSelector(selectUploadConfigData);

  const createError = useSelector(selectCreatePayoutError);
  const updateError = useSelector(selectUpdatePayoutError);
  const createdNewPayout = useSelector(selectCreatePayoutData);
  const updatePayoutData = useSelector(selectUpdatePayoutData);
  const removePayoutData = useSelector(selectRemovePayoutData);

  const [initialValues, setInitialValues] = useState<any | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // validation errors translation
  const [errorsMessages, setErrorsMessages] = useState({
    required: t("common.validation.required_field"),
    amountRequired: t("common.validation.required_field"),
    amountMax: t("common.validation.amount_max"),
    amountMin: t("common.validation.amount_min"),
    notNumber: t("common.validation.not_a_number"),
    maxLength: t("common.validation.max_length_255"),
    minLength: t("common.validation.min_length"),
  });

  useEffect(() => {
    if (!uploadConfigData) {
      getUploadConf();
    }
  }, [uploadConfigData]);

  //set initial form value
  useEffect(() => {
    if (!!availablePaymentsData) {
      if (!!context.dataCreateUpdatePayout) {
        setInitialValues({
          amount: Number(context.dataCreateUpdatePayout.amount) || '',
          comment: context.dataCreateUpdatePayout.comment || '',
          date: new Date(context.dataCreateUpdatePayout.paymentDate),
          payment_method: !!context.dataCreateUpdatePayout.walletPaymentMethodName ? {
            id: context.dataCreateUpdatePayout.walletPaymentMethodValue[context.dataCreateUpdatePayout.walletPaymentMethodDefaultField],
            showValue: context.dataCreateUpdatePayout.walletPaymentMethodValue[context.dataCreateUpdatePayout.walletPaymentMethodDefaultField],
            isDefault: false,
            defaultField: context.dataCreateUpdatePayout.walletPaymentMethodDefaultField,
            walletCurrency: context.dataCreateUpdatePayout.walletCurrency,
            paymentMethodName: context.dataCreateUpdatePayout.walletPaymentMethodName,
            paymentMethodValue: context.dataCreateUpdatePayout.walletPaymentMethodValue,
          } : '',
          walletCurrency: context.dataCreateUpdatePayout.walletCurrency || '',
          transactionId: context.dataCreateUpdatePayout.transactionId || '',
          walletAmount: context.dataCreateUpdatePayout.walletAmountNoRecalculatable || '',
        });
      } else {
        const currentPaymentMethod = availablePaymentsData.filter((item: any) => item.isDefault === true);

        setInitialValues({
          flatFeeId: flatFeeId || data.id,
          amount: Number(data.amount) || '',
          comment: '',
          date: new Date(),
          payment_method: !!currentPaymentMethod.length ? currentPaymentMethod[0] : '',
          walletCurrency: !!currentPaymentMethod.length && !!currentPaymentMethod[0].walletCurrency ? currentPaymentMethod[0].walletCurrency : '',
          transactionId: '',
          walletAmount: '',
        });
      }
    }
  }, [context.dataCreateUpdatePayout, data, availablePaymentsData])

  useEffect(() => {
    setErrorsMessages({
      required: t("common.validation.required_field"),
      amountRequired: t("common.validation.required_field"),
      amountMax: t("common.validation.amount_max"),
      amountMin: t("common.validation.amount_min"),
      notNumber: t("common.validation.not_a_number"),
      maxLength: t("common.validation.max_length"),
      minLength: t("common.validation.min_length"),
    });

    setTimeout(() => {
      !!formRef && !!formRef.current && formRef.current.validateForm();
    }, 50)
  }, [i18n.language, t]);

  useEffect(() => {
    return () => {
      clearCreatePayout();
      clearUpdatePayout();
      clearRemovePayout();
      setInitialValues({
        amount: '',
        comment: '',
        date: new Date(),
        payment_method: '',
        walletCurrency: '',
        transactionId: '',
        walletAmount: '',
      });
      clearAvailablePaymentsList();
    }
  }, []);

  useEffect(() => {
    getAvailablePaymentsList({
      affiliateId: Number(affiliateId || affiliate_id),
    })
  }, []);

  useEffect(() => {
    if (mobile && !!closeModal) {
      if (!!createdNewPayout || !!updatePayoutData || !!removePayoutData) {
        closeModal(false);
      }
    }
  }, [createdNewPayout, updatePayoutData, removePayoutData]);

  useEffect(() => {
    if (!!createError || !!updateError) {
      setIsSubmitting(false);
    }
  }, [createError, updateError]);

  const submitForm = (values: FormikValues, actions: FormikHelpers<any>): void => {
    if (!!createError || !!updateError) {
      clearCreatePayout();
      clearUpdatePayout();
    }
    if (!isSubmitting) {
      setIsSubmitting(true);

      const payload = {
        flatFeeId: flatFeeId || data.id,
        amount: String(values.amount),
        ...(!!values.comment ? { comment: String(values.comment) } : {}),
        ...(!!context.dataCreateUpdatePayout ? { paymentId: context.dataCreateUpdatePayout.id } : {}),
        paymentDate: new Date(values.date).toISOString(),
        transactionId: !!values.transactionId ? values.transactionId : null,
        walletCurrency: !!values.walletCurrency ? values.walletCurrency : null,
        walletAmount: !!values.walletAmount ? `${values.walletAmount}` : null,
        wallet: !!values.payment_method ? values.payment_method : null,
      }

      if (!!context.dataCreateUpdatePayout) {
        updatePayout(payload)
      } else {
        createPayout(payload);
      }

      actions.setSubmitting(false);
    }
  };

  //@ts-ignore
  const paymentImages = importAll(require.context('../../../../../../../assets/images/payments', false, /\.(png|jpe?g|svg)$/));

  const validationAmount = (value: any) => validationAmountField(value, errorsMessages);
  const validationWalletAmount = (value: any) => validationAmountNotRequiredField(value, errorsMessages);
  const validateComment = (value: any) => validationTextField(value, 255, errorsMessages);
  const validateCurrency = (value: any) => validationTextFieldMinMax(value, 3, 8, errorsMessages);
  const validateTransactionId = (value: any) => validationTextField(value, 255, errorsMessages);
  const validatePaymentMethodField = (value: any) => validationRequiredAutocompleteField(value, errorsMessages);
  const validateRequiredAutocompleteSelectField = (value: any) => validationSingleSelectField(value, errorsMessages);

  return (
    <>
      {!!initialValues && !!availablePaymentsData && (
        <Box className={styles.block}>
          {mobile && (
            <Typography className={styles.title} variant="h6">
              {t("admin.reports.detailed_fin_report.payouts.mobile_title")}
            </Typography>
          )}

          <Box className={styles.content}>
            <Formik
              validateOnChange
              validateOnBlur
              validateOnMount
              enableReinitialize
              innerRef={formRef}
              initialValues={initialValues}
              onSubmit={ (values, actions) => submitForm(values, actions) }
            >
              { props => {
                const { values, handleSubmit, handleChange, isValid, dirty, errors } = props;

                const disableSubmit = !isValid;

                return (
                  <Form className={styles.form} onSubmit={handleSubmit}>
                    <Box className={styles.field}>
                      <MuiSingleDateTimePicker
                        id="date"
                        name="date"
                        autoComplete="date"
                        label={t("admin.reports.detailed_fin_report.payouts.form.date")}
                        value={values.date}
                        maxDate={new Date()}
                        views={["month", "year", "day", "hours", "minutes"]}
                      />
                    </Box>

                    <Box className={styles.field}>
                      <MuiTextField
                        size="small"
                        id="amount"
                        type="number"
                        name="amount"
                        autoComplete="amount"
                        label={t("admin.reports.detailed_fin_report.payouts.form.amount_in_platform_currency")}
                        value={values.amount}
                        validate={(value) => validationAmount(value)}
                        onChange={handleChange}
                        startAdornment={(!!context.convertCurrency.baseCurrency && (!!getSymbolFromCode(context.convertCurrency.baseCurrency.toUpperCase()) ? getSymbolFromCode(context.convertCurrency.baseCurrency.toUpperCase()) : context.convertCurrency.baseCurrency)) || '$'}
                      />
                    </Box>

                    <Box className={styles.field}>
                      <SelectIconsAutocomplete
                        keyValue="paymentMethodName"
                        showValue='showValue'
                        keyId="id"
                        id="payment_method"
                        name="payment_method"
                        label={t(`common.components.payment.form.wallet`)}
                        value={values.payment_method}
                        multiple={false}
                        showSelectAll={false}
                        blurOnSelect={true}
                        staticOptions={availablePaymentsData}
                        images={paymentImages}
                      />
                    </Box>

                    <Box className={styles.field}>
                      <MuiTextField
                        size="small"
                        id="walletCurrency"
                        type="text"
                        name="walletCurrency"
                        autoComplete="walletCurrency"
                        label={t("common.components.payment.form.walletCurrency")}
                        value={values.walletCurrency}
                        onChange={handleChange}
                        validate={validateCurrency}
                        multiline={false}
                        disabled={!!values?.payment_method?.walletCurrency}
                      />
                    </Box>

                    <Box className={styles.field}>
                      <MuiTextField
                        size="small"
                        id="walletAmount"
                        type="number"
                        name="walletAmount"
                        autoComplete="walletAmount"
                        label={t("common.components.payment.form.walletAmount")}
                        value={values.walletAmount}
                        validate={(value) => validationWalletAmount(value)}
                        onChange={handleChange}
                        onKeyDown={(e) => exceptThisSymbols.includes(e.key) && e.preventDefault()}
                        disabled={!!values.payment_method && !!values.payment_method.walletCurrency && values.payment_method.walletCurrency.toLowerCase() === uploadConfigData.defaultCurrency.toLowerCase()}
                      />
                    </Box>

                    <Box className={styles.field}>
                      <MuiTextField
                        size="small"
                        id="transactionId"
                        type="text"
                        name="transactionId"
                        autoComplete="transactionId"
                        label={t("common.components.payment.form.transactionNumber")}
                        value={values.transactionId}
                        onChange={handleChange}
                        validate={validateTransactionId}
                        multiline={false}
                      />
                    </Box>

                    <Box className={styles.fieldLarge}>
                      <MuiTextField
                        size="small"
                        id="comment"
                        type="text"
                        name="comment"
                        autoComplete="comment"
                        label={t("admin.reports.detailed_fin_report.payouts.form.comment")}
                        value={values.comment}
                        onChange={handleChange}
                        validate={validateComment}
                        multiline={false}
                      />
                    </Box>

                    <HelperField
                      values={values}
                      baseCurrency={uploadConfigData.defaultCurrency}
                    />

                    <Box className={styles.actions}>
                      <Button
                        gradient
                        fullWidth
                        disableElevation
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting || disableSubmit}
                      >{t("common.buttons.apply")}</Button>
                    </Box>
                  </Form>
                )
              }}
            </Formik>
            {!!createError && !!createError.error && (
              <Alert sx={{ marginBottom: 2, marginTop: 2 }} variant="outlined" severity="error">{t(createError.error)}</Alert>
            )}
            {!!updateError && !!updateError.error && (
              <Alert sx={{ marginBottom: 2, marginTop: 2 }} variant="outlined" severity="error">{t(updateError.error)}</Alert>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default CreateUpdatePayout;
